/**
 * Query keys, in one place.
 *
 * Invalidation is only as good as the keys agreeing: a mutation that
 * invalidates `['courses']` while the list reads `['teach', 'courses']` fails
 * silently, and the symptom is a stale screen rather than an error.
 */

export const queryKeys = {
  me: ['me'] as const,
  /** Feature configuration — read once at boot, never invalidated at runtime. */
  config: ['config'] as const,

  /**
   * One email-verification attempt, keyed by the token in the link.
   *
   * ⚠️ **The token is the key because the token is the question.** The link is
   * single-use, so spending it can be done once and the answer has to be
   * remembered against that exact token — a second request with the same one
   * gets a 400 no matter what the first returned.
   */
  emailVerification: (token: string) => ['auth', 'verify-email', token] as const,

  /** The signed-in user's own pending email change, if any — `GET /me/email/change/`. */
  pendingEmailChange: ['me', 'email-change'] as const,

  /**
   * One email-change confirmation attempt, keyed by the token — same shape and
   * same reasoning as `emailVerification` above: the link is single-use, so the
   * answer has to be remembered against the exact token that spent it.
   */
  emailChangeConfirm: (token: string) => ['auth', 'email-change', 'confirm', token] as const,
  /** The cancel counterpart, keyed the same way. */
  emailChangeCancel: (token: string) => ['auth', 'email-change', 'cancel', token] as const,

  categories: ['categories'] as const,
  skills: ['skills'] as const,
  professions: ['professions'] as const,
  /** One public profile, by handle. */
  publicProfile: (handle: string) => ['profiles', handle] as const,

  /** Every authoring list, whatever its filters — the invalidation target. */
  teachCourses: ['teach', 'courses'] as const,
  teachCourseList: (filters: Record<string, unknown> = {}) =>
    ['teach', 'courses', 'list', filters] as const,
  teachCourse: (id: string) => ['teach', 'courses', id] as const,
}
